<template>
    <!-- 手机端我的客户 -->
    <ZhaiHead />

    <div class="container-fluid px-0 zhu">
        <!-- 顶部 -->
        <div class="promo-top">
            <div class="promo-title" @click="goBack">
                <i class="fa fa-bullhorn"></i>推广中心
            </div>
        </div>

        <!-- 内容区（内部滚动容器） -->
        <div class="customers-wrap" ref="customersWrapRef">
            <!-- 分段切换 -->
            <div class="segmented">
                <button
                        class="seg-btn"
                        :class="{ active: activeTab === 'direct' }"
                        @click="switchTab('direct')"
                >
                    直推客户
                </button>
                <button
                        class="seg-btn"
                        :class="{ active: activeTab === 'indirect' }"
                        @click="switchTab('indirect')"
                >
                    间接客户
                </button>
            </div>

            <!-- 列表 -->
            <div class="list">
                <div
                        v-for="item in pagedList"
                        :key="item.id"
                        class="list-item"
                        @click="openCustomer(item)"
                >
                    <img class="avatar" :src="item.avatar" alt="avatar" />
                    <div class="info">
                        <div class="name">{{ item.name }}</div>
                    </div>
                    <div class="right">
                        <span class="time">{{ item.time }}</span>
                        <i class="fa fa-angle-right arrow"></i>
                    </div>
                </div>

                <!-- Loading -->
                <div v-if="currentState.loading" class="empty">加载中...</div>

                <!-- 空态 -->
                <div v-else-if="pagedList.length === 0" class="empty">
                    暂无{{ activeTab === "direct" ? "直推" : "间接" }}客户
                </div>
            </div>
        </div>

        <!-- 页码条 -->
        <div class="pager-bar" v-if="currentState.total > 0">
            <SimplePager
                    v-model="pageMap[activeTab]"
                    :total="currentState.total"
                    :pageSize="pageSize"
                    :maxButtons="7"
                    @change="onPageChange"
            />
        </div>
    </div>

    <!-- ✅ 解绑确认模态框 -->
    <div
            v-if="unbindModal.visible"
            class="modal-mask"
            @click.self="closeUnbindModal"
    >
        <div class="modal-card" role="dialog" aria-modal="true">
            <div class="modal-title">解绑客户？</div>
            <div class="modal-desc">解绑后将无法继续获取其分销佣金</div>

            <div class="modal-actions">
                <button class="modal-btn cancel" @click="closeUnbindModal" :disabled="unbindModal.loading">
                    取消
                </button>
                <button class="modal-btn ok" @click="confirmUnbind" :disabled="unbindModal.loading">
                    {{ unbindModal.loading ? "处理中..." : "确定" }}
                </button>
            </div>
        </div>
    </div>

    <ZhaiFoot />
</template>

<script setup>
    import { ref, reactive, computed, watch, nextTick, defineComponent, h, onMounted, onBeforeUnmount } from "vue";
    import ZhaiHead from "../components/ZhaiHead";
    import ZhaiFoot from "../components/ZhaiFoot";
    import http from "../utils/http.js";

    /** -----------------------------
     *  内嵌分页组件 SimplePager
     *  ✅ 页码外码逻辑参考 PC：首尾 + 当前邻居 + 省略号
     *  ----------------------------- */
    const SimplePager = defineComponent({
        name: "SimplePager",
        props: {
            modelValue: { type: Number, default: 1 },
            total: { type: Number, required: true },
            pageSize: { type: Number, default: 10 },
            maxButtons: { type: Number, default: 7 }, // 建议 7（与你 PC 一致体验）
        },
        emits: ["update:modelValue", "change"],
        computed: {
            totalPages() {
                return Math.max(1, Math.ceil(this.total / this.pageSize));
            },
            pageItems() {
                const total = this.totalPages;
                const cur = this.modelValue;

                if (total <= 1) return [];
                // total 小时直接铺满
                if (total <= this.maxButtons) return Array.from({ length: total }, (_, i) => i + 1);

                // 参考 PC 的 pageItems 逻辑（7 按钮为例）
                if (cur <= 2) return [1, 2, 3, "...", total];
                if (cur >= total - 1) return [1, "...", total - 2, total - 1, total];

                return [1, "...", cur - 1, cur, cur + 1, "...", total];
            },
        },
        methods: {
            toPage(p) {
                const page = Math.min(Math.max(1, p), this.totalPages);
                if (page !== this.modelValue) {
                    this.$emit("update:modelValue", page);
                    this.$emit("change", page);
                }
            },
        },
        render() {
            if (this.totalPages <= 1) return null;

            const makeBtn = (text, opts = {}) =>
                h(
                    "button",
                    {
                        class: ["pg-btn", opts.class],
                        disabled: !!opts.disabled,
                        onClick: opts.onClick,
                    },
                    text
                );

            const pageButtons = this.pageItems.map((p) => {
                if (p === "...") return makeBtn("...", { class: "ellipsis", disabled: true });
                return makeBtn(String(p), {
                    class: p === this.modelValue ? "active" : "",
                    onClick: () => this.toPage(p),
                });
            });

            return h("div", { class: "pager" }, [
                makeBtn("«", {
                    class: "pg-nav",
                    disabled: this.modelValue <= 1,
                    onClick: () => this.toPage(this.modelValue - 1),
                }),
                ...pageButtons,
                makeBtn("»", {
                    class: "pg-nav",
                    disabled: this.modelValue >= this.totalPages,
                    onClick: () => this.toPage(this.modelValue + 1),
                }),
            ]);
        },
    });

    /** -----------------------------
     *  页面状态
     *  ----------------------------- */
    const activeTab = ref("direct");
    const pageSize = 10;

    const customersWrapRef = ref(null);

    const pageMap = reactive({
        direct: 1,
        indirect: 1,
    });

    const store = reactive({
        direct: { list: [], total: 0, loading: false, loaded: false },
        indirect: { list: [], total: 0, loading: false, loaded: false },
    });

    const currentState = computed(() => store[activeTab.value]);

    // 你当前手机端没有搜索框，这里保持“前端分页展示”
    const pagedList = computed(() => {
        const page = pageMap[activeTab.value];
        const start = (page - 1) * pageSize;
        return (currentState.value.list || []).slice(start, start + pageSize);
    });

    /** -----------------------------
     *  工具：滚动回顶（保持你原体验）
     *  ----------------------------- */
    async function scrollToTop({ immediate = false, smooth = false } = {}) {
        const el = customersWrapRef.value;
        if (!el) return;
        if (!immediate) await nextTick();
        el.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
    }

    /** -----------------------------
     *  ✅ userId 兜底（对齐 PC 项目风格）
     *  ----------------------------- */
    function resolveUserIdSafe() {
        // 1) localStorage 常见存法
        const tryKeys = ["user", "userinfo", "userInfo", "USER", "User"];
        for (const k of tryKeys) {
            try {
                const raw = localStorage.getItem(k);
                if (!raw) continue;
                const obj = JSON.parse(raw);
                const id = obj?.id ?? obj?.userId ?? obj?.data?.id ?? obj?.data?.userId;
                if (id != null && id !== "") return Number(id);
            } catch (e) {
                // ignore
            }
        }
        // 2) 取不到返回 null，后端若支持 token 解析也能跑
        return null;
    }

    /** -----------------------------
     *  ✅ 接口：customers 列表（对齐 PC）
     *  你 Network 已有：
     *  /platform/promo/customers?userId=9&type=direct&pageNum=1&pageSize=10&keyword=
     *
     *  手机端仍用“前端分页”，因此一次拉全量 pageSize=9999
     *  ----------------------------- */
    async function apiFetchCustomers(type) {
        const token = localStorage.getItem("token");
        if (!token) {
            window.location.href = "/login";
            return { list: [], total: 0 };
        }

        const userId = resolveUserIdSafe();
        if (!userId) {
            // 和你 PC 版一致（你 PC 用 alert）
            alert("userId不可为空（未获取到用户信息）");
            return { list: [], total: 0 };
        }

        const res = await http.get("/platform/promo/customers", {
            userId,
            type,
            pageNum: 1,
            pageSize: 9999,
            keyword: "", // 手机端无搜索 UI，保持空；以后要加搜索再接
        });

        const json = res?.data;
        if (!json || Number(json.status) !== 1) {
            throw new Error(json?.msg || "加载客户失败");
        }

        const d = json.data || {};
        const rawList = d.list || d?.pageInfo?.list || [];
        const total =
            Number(d.total) ||
            Number(d?.pageInfo?.total) ||
            Number(d.count) ||
            (Array.isArray(rawList) ? rawList.length : 0);

        const list = (rawList || []).map((x) => {
            const id = x.id ?? x.userId ?? x.customerId;
            const name = x.username ?? x.nickname ?? x.name ?? (id != null ? `用户${id}` : "用户");
            const time =
                x.time ||
                x.bindTime || x.bind_time ||
                x.createTime || x.create_time ||
                x.updateTime || x.update_time ||
                "—";
            const avatar =
                x.avatar ||
                x.headImg ||
                x.head_img ||
                "https://dummyimage.com/80x80/cccccc/ffffff.png&text=🙂";

            return { id, name, time, avatar };
        });

        return { list, total };
    }

    /** -----------------------------
     *  ✅ 接口：解绑（对齐 PC）
     *  POST /platform/promo/unbind?userId=9
     *  body: { customerId, type }
     *  ----------------------------- */
    async function apiUnbindCustomer(payload) {
        const token = localStorage.getItem("token");
        if (!token) {
            window.location.href = "/login";
            return null;
        }

        const userId = resolveUserIdSafe();
        if (!userId) {
            alert("userId不可为空（未获取到用户信息）");
            return null;
        }

        const res = await http.post(`/platform/promo/unbind?userId=${userId}`, payload);
        return res?.data;
    }

    /** -----------------------------
     *  加载 tab（缓存 + force 刷新，参考 PC）
     *  ----------------------------- */
    async function loadTab(type, { force = false } = {}) {
        const st = store[type];
        if (st.loading) return;
        if (st.loaded && !force) return;

        st.loading = true;
        try {
            const { list, total } = await apiFetchCustomers(type);
            st.list = Array.isArray(list) ? list : [];
            st.total = Number(total || st.list.length || 0);
            st.loaded = true;

            // 列表更新后，修正页码，避免越界
            const tp = Math.max(1, Math.ceil((st.total || 0) / pageSize));
            if (pageMap[type] > tp) pageMap[type] = tp;
        } finally {
            st.loading = false;
        }
    }

    /** -----------------------------
     *  交互：切换 tab（参考你现有逻辑，切前回顶）
     *  ----------------------------- */
    async function switchTab(type) {
        if (activeTab.value === type) return;

        await scrollToTop({ immediate: true, smooth: false });
        activeTab.value = type;
    }

    // tab 变化：重置页码 + 拉取 + 回顶（与你原逻辑一致）
    watch(activeTab, async (t) => {
        pageMap[t] = 1;
        await loadTab(t);
        await scrollToTop({ immediate: false, smooth: false });
    });

    /** -----------------------------
     *  翻页：回顶
     *  ----------------------------- */
    async function onPageChange() {
        await scrollToTop({ immediate: false, smooth: true });
    }

    /** -----------------------------
     *  返回
     *  ----------------------------- */
    function goBack() {
        if (window.history.length > 1) window.history.back();
        else window.location.href = "/Promotion";
    }

    /** -----------------------------
     *  ✅ 解绑模态框（与 PC 逻辑对齐：loading 时默认不允许关，但提供 force）
     *  ----------------------------- */
    const unbindModal = reactive({
        visible: false,
        loading: false,
        customer: null,
    });

    function openUnbindModal(customer) {
        unbindModal.customer = customer;
        unbindModal.visible = true;
    }

    function closeUnbindModal(force = false) {
        if (unbindModal.loading && !force) return;
        unbindModal.visible = false;
        unbindModal.customer = null;
    }

    // 点击客户：弹出解绑确认框（保持你 template 行为）
    function openCustomer(item) {
        openUnbindModal(item);
    }

    /** -----------------------------
     *  ✅ 解绑确认（对齐 PC：本地移除 + 页码修正 + 强制刷新 + 强制关弹窗）
     *  ----------------------------- */
    async function confirmUnbind() {
        if (!unbindModal.customer || unbindModal.loading) return;

        unbindModal.loading = true;
        try {
            const customer = unbindModal.customer;

            const payload = {
                customerId: customer.id,
                type: activeTab.value, // direct / indirect
            };

            const res = await apiUnbindCustomer(payload);

            if (res && Number(res.status) === 1) {
                // 1) 本地先移除
                const st = store[activeTab.value];
                st.list = (st.list || []).filter((x) => x.id !== customer.id);
                st.total = Math.max(0, (st.total || 0) - 1);

                // 2) 修正页码，避免越界
                const tp = Math.max(1, Math.ceil((st.total || 0) / pageSize));
                if (pageMap[activeTab.value] > tp) pageMap[activeTab.value] = tp;

                // 3) 强制关弹窗
                closeUnbindModal(true);

                // 4) 强制刷新一次，保证与后端一致
                await loadTab(activeTab.value, { force: true });

                // 5) 回顶
                await scrollToTop({ immediate: false, smooth: false });
            } else {
                alert(res?.msg || "解绑失败");
            }
        } catch (e) {
            console.error(e);
            alert("解绑失败（网络异常）");
        } finally {
            unbindModal.loading = false;
        }
    }

    /** -----------------------------
     *  生命周期：首次进入加载 direct
     *  ----------------------------- */
    onMounted(async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            window.location.href = "/login";
            return;
        }

        await loadTab("direct", { force: true });
        await scrollToTop({ immediate: false, smooth: false });
    });

    onBeforeUnmount(() => {
        // 这里目前没有 interval / observer，无需额外清理
    });
</script>

<style scoped>
    .fa {
        margin-right: 6px;
    }

    /* 页面主体 */
    .zhu {
        margin-top: 80px;
        height: calc(100vh - 80px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .promo-title {
        padding: 12px;
        font-weight: 400;
        border-bottom: 1px solid #e6e6e6;
        cursor: pointer;
        user-select: none;
    }
    .promo-title:hover {
        color: #409eff;
    }
    .promo-title:active {
        opacity: 0.7;
    }

    /* 内容区：内部滚动容器 */
    .customers-wrap {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        padding: 14px;
        background: #f6f7fb;
    }

    /* 分段切换 */
    .segmented {
        display: flex;
        width: 100%;
        max-width: 520px;
        margin: 0 auto 14px;
        border: 1px solid #d7dbe6;
        background: #fff;
        border-radius: 999px;
        padding: 4px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    }
    .seg-btn {
        flex: 1;
        height: 40px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: #6b7280;
        font-weight: 500;
        font-size: 14px;
        letter-spacing: 0.5px;
        cursor: pointer;
        transition: all 0.15s ease;
    }
    .seg-btn.active {
        background: #00c26f;
        color: #ffffff;
        font-weight: 600;
        box-shadow: 0 2px 6px rgba(0, 194, 111, 0.35);
    }
    .seg-btn:active {
        transform: scale(0.99);
    }

    /* 列表 */
    .list {
        background: #fff;
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid #eef0f5;
    }
    .list-item {
        display: flex;
        align-items: center;
        padding: 14px 12px;
        border-bottom: 1px solid #f0f2f7;
        cursor: pointer;
    }
    .list-item:last-child {
        border-bottom: 0;
    }
    .avatar {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        object-fit: cover;
        margin-right: 12px;
        background: #e5e7eb;
    }
    .info {
        flex: 1;
        min-width: 0;
    }
    .name {
        font-size: 12px;
        font-weight: 500;
        color: #374151;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .right {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #9ca3af;
        font-size: 11px;
    }
    .arrow {
        font-size: 18px;
        color: #c7cad1;
    }
    .list-item:active {
        background: #f7f8fb;
    }

    /* 空态/加载 */
    .empty {
        padding: 40px 12px;
        text-align: center;
        color: #9aa1ad;
        font-size: 14px;
    }

    /* ================== 分页（参考 PC 风格：替换掉原手机 pager 视觉） ================== */

    /* 页码条：保持底部容器，但使用 PC 视觉 */
    .pager-bar {
        position: static !important;
        z-index: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        background: #f6f7fb;
        backdrop-filter: none;
        border-top: 1px solid #e9ecf3;
    }

    /* PC 风格 pager 容器（2px 圆角与项目统一） */
    .pager {
        display: inline-flex;
        align-items: center;
        border: 1px solid #e5e7eb;
        overflow: hidden;
        background: #fff;
        border-radius: 2px;
    }

    /* PC 风格按钮 */
    .pg-btn {
        min-width: 36px;
        height: 32px;
        padding: 0 8px;
        border: 0;
        background: transparent;
        color: #6b7280;
        font-size: 12px;
        font-weight: 400;
        cursor: pointer;
        border-right: 1px solid #eef0f5;
    }

    .pg-btn:last-child {
        border-right: 0;
    }

    /* 当前页 */
    .pg-btn.active {
        background: #111827;
        color: #fff;
        font-weight: 500;
    }

    /* 不可点 */
    .pg-btn:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    /* 左右翻页按钮 */
    .pg-nav {
        background: #eef2f7;
        color: #6b7280;
    }

    /* 省略号按钮 */
    .pg-btn.ellipsis {
        cursor: default;
        color: #9ca3af;
    }

    /* 点击反馈（克制） */
    .pg-btn:active:not(:disabled):not(.ellipsis) {
        background: rgba(17, 24, 39, 0.06);
    }

    /* ✅ 模态框：遮罩 */
    .modal-mask {
        position: fixed;
        inset: 0;
        z-index: 9999;
        background: rgba(0, 0, 0, 0.55);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 18px;
    }

    /* ✅ 模态框：卡片 */
    .modal-card {
        width: 100%;
        max-width: 520px;
        background: #fff;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 18px 60px rgba(0, 0, 0, 0.25);
    }

    /* 标题/文案 */
    .modal-title {
        text-align: center;
        font-size: 18px;
        font-weight: 700;
        padding: 20px 18px 10px;
        color: #111827;
    }
    .modal-desc {
        text-align: center;
        font-size: 14px;
        color: #6b7280;
        padding: 0 18px 18px;
    }

    /* 底部按钮区：左右各一半 */
    .modal-actions {
        display: flex;
        border-top: 1px solid #eef0f5;
    }
    .modal-btn {
        flex: 1;
        height: 52px;
        border: 0;
        background: #fff;
        font-size: 16px;
        cursor: pointer;
    }
    .modal-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    /* 左右分割线 */
    .modal-btn.cancel {
        color: #6b7280;
        border-right: 1px solid #eef0f5;
    }
    .modal-btn.ok {
        color: #22c55e;
        font-weight: 700;
    }
    .modal-btn:active:not(:disabled) {
        background: #f6f7fb;
    }
</style>
